import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { InstallerButtonsBaseComponent } from "@mypxplat/xplat/features";
import { UserService, GAService, ProductService, WindowService, ProductSnapshot, ProductLicenseItem, environment } from "@mypxplat/xplat/core";
import { AppService, AvoService } from "@mypxplat/xplat/web/core";
import { DownloadVersionsModalComponent, PreviousVersionsComponent, IlokComponent, FirmwareInstructionsComponent } from "../modals";

interface EnrichedRelease {
  version?: string;
  buildNumber?: number;
  releaseDate?: string;
  platforms: any[];
}

interface InstallerGroup {
  type: string;
  isOwnProduct: boolean;
  // True only when at least one Mac/Windows file exists anywhere in latest + previousReleases
  // — see the "More versions" gating comment in ngOnInit for why this isn't just
  // `!!previousReleases.length`.
  hasHistory: boolean;
  latest: EnrichedRelease;
  previousReleases: EnrichedRelease[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-installer-buttons",
  templateUrl: "installer-buttons.component.html",
  standalone: false,
})
export class InstallerButtonsComponent extends InstallerButtonsBaseComponent implements OnInit {
  @Input() softwareDownloads: ProductSnapshot["softwareDownloads"];
  @Input() downloadableContent: ProductSnapshot["downloadableContent"];
  @Input() showAllSystems: boolean;
  @Input() product: ProductSnapshot["product"];
  @Input() license: ProductLicenseItem;
  @Input() dealer: boolean = false;
  @Input() beta: boolean;
  @Input() betaOnly: boolean;
  @Input() isSideBar: boolean = true;
  @Input() centered: boolean = false;
  @Input() showManageActivations: boolean = false;
  public hasNoInstallers: boolean = true;
  public myOs: string;

  // One entry per software family found on this snapshot's softwareDownloads (Capture,
  // Universal Control, or — for a product that IS software — the product's own single
  // group). Each platform item under `latest`/`previousReleases` is enriched with
  // osGroup/osLabel/osIcon (see _describeOs) so the template never has to call a method —
  // this codebase's convention is pre-computed values, not method calls, in bindings.
  public installerGroups: InstallerGroup[] = [];
  // Firmware's `latest`, one platform item (mirrors the old firmwareList[0] behavior — no OS
  // filtering; firmware isn't a per-OS choice the way installers are).
  public firmwareLatest: any = null;
  // Only populated in betaOnly mode: every beta-flagged firmware file across latest + history.
  public betaFirmwareList: any[] = [];
  public releaseNotesItem: any = null;
  public hasMacInstaller: boolean = false;
  public hasWinInstaller: boolean = false;
  /** True while we check Dynamo for firmware instructions before opening the modal. */
  public checkingFirmwareInstructions = false;

  constructor(
    userService: UserService,
    public appService: AppService,
    public ga: GAService,
    public productService: ProductService,
    private _win: WindowService,
    public avoService: AvoService,
    public translate: TranslateService
  ) {
    super(userService);
  }

  private fireFileDownloaded(download: any) {
    const te = this.avoService.trackEvent();
    if (!te) return;
    const page = this.avoService.getPageProperties({ pageName: "Product Detail" });
    const extra = {
      sku: this.product?.stringId || this.product?.id?.toString() || "",
      productName: this.product?.title || "",
      category: "download",
      ctaLocation: "Product Detail",
      ctaText: download?.title || "Download",
    };
    const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
    if (this.userService.appId === "fender") {
      if (typeof (te as any).fileDownloadedMyFender === "function") {
        (te as any).fileDownloadedMyFender(payload);
      }
    } else if (typeof te.fileDownloaded === "function") {
      te.fileDownloaded(payload);
    }
  }

  // The legacy ContentArchive-backed getPreviousVersions endpoint only has data for Studio
  // One/Studio Pro THE PRODUCT — never a hardware product that merely bundles a Studio One
  // companion app. Confirmed against real data twice: (1) Tone Master Pro's own product
  // record is licenseScheme 0 too, but its "Pro Control" companion software's real history
  // already flows correctly through softwareDownloads.previousReleases, so licenseScheme alone
  // was wrong; (2) StudioLive 32SC Series III (hardware) bundles a "Studio One" group (the
  // Studio One Remote Android app) that matched on group-name alone, wrongly showing "More
  // Studio One versions" and routing to getPreviousVersions with the MIXER's product ID/
  // licenseScheme — an endpoint that only ever has data for the real Studio One/Studio Pro
  // product. `isOwnProduct` is exactly "is this group the product itself, not a bundled
  // companion" (set below via the categoryId:"software" shortcut, where group.type ===
  // product.title) — require it alongside the name match.
  private _isLegacyVersionedFamily(groupType: string, isOwnProduct: boolean): boolean {
    if (!isOwnProduct) return false;
    const type = (groupType || "").toLowerCase();
    return type.includes("studio one") || type.includes("studioone") || type.includes("studio pro");
  }

  ngOnInit() {
    this.myOs = this.appService.os;

    // PIM data is frequently missing version/releaseDate on individual platform items (real
    // example: a release's Windows file has both, but its Mac/Android siblings from the same
    // release have empty strings for both) — fall back to the release-level version/releaseDate
    // (derived by the backend from whichever item in the release DID have them) rather than
    // rendering "v" with nothing after it, or feeding an empty string to the `date` pipe.
    const enrichRelease = (release: any) => ({
      ...release,
      platforms: (release?.platforms || []).map((p: any) => ({
        ...p,
        ...this._describeOs(p.platform),
        version: p.version || release?.version,
        releaseDate: p.releaseDate || release?.releaseDate,
      })),
    });
    const allGroups: InstallerGroup[] = (this.softwareDownloads || []).map((g: any) => {
      const latest = enrichRelease(g.latest);
      const previousReleases = (g.previousReleases || []).map(enrichRelease);
      // The "More versions" modal only offers Mac/Windows filters (see
      // DownloadVersionsModalComponent) — a group whose entire history is some other platform
      // (e.g. an Android-only companion app) would show a button that opens on an empty list
      // for every filter. Confirmed against real data (Retro Mix Legends): checking `latest`
      // here too, not just `previousReleases`, means the button shows even when there's
      // nothing MORE to see beyond what's already displayed as the current install — only
      // `previousReleases` actually represents additional history.
      const hasDesktopHistory = previousReleases.some((r: any) => r.platforms.some((p: any) => p.osGroup === "mac" || p.osGroup === "win"));
      const isOwnProduct = g.type === this.product?.title;
      return {
        type: g.type,
        isOwnProduct,
        hasHistory: this._isLegacyVersionedFamily(g.type, isOwnProduct) || hasDesktopHistory,
        latest,
        previousReleases,
      };
    });

    const firmwareGroup = allGroups.find((g) => g.type === "Firmware") || null;
    let installerGroups = allGroups.filter((g) => g.type !== "Firmware");

    const releaseNotesGroup = (this.downloadableContent || []).find((g) => g.type === "Release Notes");
    this.releaseNotesItem = releaseNotesGroup?.content?.[0] || null;

    if (this.betaOnly) {
      // A beta build isn't necessarily each platform's newest — promoteLatestPerPlatform keeps
      // whichever build is newest per platform in `latest`, and a public release with a higher
      // build number routinely supersedes an older beta there, pushing the beta itself back
      // into `previousReleases` (confirmed against real data: StudioLive 32S Series III's
      // Capture beta, v3.1.0.66460, sits one release back behind the public v3.2.0.106606).
      // Scanning only `latest.platforms` would silently drop every such beta — search the
      // group's full history instead, same as betaFirmwareList already does below.
      installerGroups = installerGroups
        .map((g) => ({ ...g, latest: { platforms: [g.latest, ...g.previousReleases].flatMap((r: any) => r.platforms).filter((p: any) => p.beta) } }))
        .filter((g) => g.latest.platforms.length);
      this.betaFirmwareList = [firmwareGroup?.latest, ...(firmwareGroup?.previousReleases || [])]
        .filter(Boolean)
        .flatMap((r: any) => r.platforms)
        .filter((p: any) => p.beta);
      this.firmwareLatest = null;
    } else {
      this.betaFirmwareList = [];
      this.firmwareLatest = firmwareGroup?.latest?.platforms?.[0] || null;
    }

    this.installerGroups = installerGroups;
    this.hasNoInstallers = !this.installerGroups.some((g) => g.latest.platforms.length);
    this.hasMacInstaller = this.installerGroups.some((g) => g.latest.platforms.some((p: any) => p.osGroup === "mac"));
    this.hasWinInstaller = this.installerGroups.some((g) => g.latest.platforms.some((p: any) => p.osGroup === "win"));
  }

  /**
   * Maps a best-guess/ground-truth `platform` value (mac, win/win64, win-arm64/winarm64, linux,
   * flatpak, android, other) onto the display fields the template needs. `osGroup` is what
   * gets compared against `myOs` for "is this my system"; `osLabel` is the exact phrase the
   * old per-OS template blocks used to hardcode (Windows (64 bit) / Windows (ARM64) / Linux
   * (deb) / Linux (flatpak)), preserved so nothing visibly changes for a real installer.
   * `win` and `win64` are the same real bucket (64-bit Windows) under two different legacy key
   * spellings — confirmed against real data: older products (Studio One 4) use `win64`, newer
   * ones (Studio One 7) use `win` — both must map to the same osGroup/osLabel.
   */
  private _describeOs(platform: string): { osGroup: string; osLabel: string; osIcon: "apple" | "windows" | "linux" | null } {
    const p = (platform || "").toLowerCase();
    if (p === "mac") return { osGroup: "mac", osLabel: "Mac", osIcon: "apple" };
    if (p === "win" || p === "win64") return { osGroup: "win", osLabel: "Windows (64 bit)", osIcon: "windows" };
    if (p === "winarm64" || p === "win-arm64") return { osGroup: "win", osLabel: "Windows (ARM64)", osIcon: "windows" };
    if (p === "linux") return { osGroup: "linux", osLabel: "Linux (deb)", osIcon: "linux" };
    if (p === "flatpak") return { osGroup: "linux", osLabel: "Linux (flatpak)", osIcon: "linux" };
    if (p === "android") return { osGroup: "android", osLabel: "Android", osIcon: null };
    return { osGroup: "other", osLabel: "Installer", osIcon: null };
  }

  showVersionsModal(group: InstallerGroup) {
    // Studio One/Studio Pro's real version history lives entirely in the legacy
    // ContentArchive table (`previousVersions`/`getPreviousVersions`) — it was never migrated
    // into softwareDownloads' `previousReleases`, so that array is always empty for this
    // family and the generic modal below has nothing to show. Keep using the old, richer modal
    // (per-version dropdown, Mac/Win/Win64/WinARM64/Linux/Flatpak) for this family specifically;
    // every other product/family's history — including Tone Master Pro's "Pro Control" and a
    // hardware product's bundled "Studio One" companion app (e.g. StudioLive 32SC's Studio One
    // Remote) — does flow through softwareDownloads correctly and must NOT be routed here.
    if (this._isLegacyVersionedFamily(group.type, group.isOwnProduct)) {
      const modalRef = this.appService.showModal(PreviousVersionsComponent, { size: "lg" });
      modalRef.componentInstance.productId = this.product.id;
      modalRef.componentInstance.licenseScheme = this.product.licenseScheme;
      return;
    }
    const modalRef = this.appService.showModal(DownloadVersionsModalComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.title = `${group.type} — All versions`;
    modalRef.componentInstance.versions = [group.latest, ...group.previousReleases].flatMap((r) => r.platforms);
  }

  showiLokOverlay() {
    const modalRef = this.appService.showModal(IlokComponent, { size: "lg" });
  }

  /**
   * Which id the firmware-instructions endpoint needs: dealers look up by PRODUCT id
   * (`isDealerProduct=true`, no ownership check); owners by their LICENSE id (paeapi checks
   * ownership). Beta-only views and owners without a license have nothing to look up.
   */
  private _firmwareInstructionsId(): number | undefined {
    if (this.betaOnly) return undefined;
    if (this.dealer) return this.product?.id ? Number(this.product.id) : undefined;
    return this.license?.licenseId;
  }

  /**
   * Shows the instructions modal when the product has instructions; otherwise — no instructions,
   * no usable id, or the check itself failing — downloads the firmware file directly, so the
   * user always ends up with the firmware.
   */
  showFirmwareInstructions(ware) {
    const productId = this._firmwareInstructionsId();
    if (!productId || !ware?.version) {
      this.downloadTokenizedAsset(ware);
      return;
    }

    if (this.checkingFirmwareInstructions) return;

    this.checkingFirmwareInstructions = true;
    this.productService.getFirmwareInstructions(productId, ware.version, this.dealer).subscribe({
      next: (result: any) => {
        this.checkingFirmwareInstructions = false;
        if (result?.hasInstructions && result?.content) {
          const modalRef = this.appService.showModal(FirmwareInstructionsComponent, { size: "lg" });
          modalRef.componentInstance.content = result.content;
          modalRef.componentInstance.loadingContent = false;
          modalRef.componentInstance.productDetails = this.product;
          modalRef.componentInstance.firmware = {
            product_id: productId,
            version: ware.version,
            dealer: this.dealer,
          };
        } else {
          this.downloadTokenizedAsset(ware);
        }
      },
      error: () => {
        this.checkingFirmwareInstructions = false;
        this.downloadTokenizedAsset(ware);
      },
    });
  }

  downloadTokenizedAsset(download) {
    if (download.title.indexOf("Notion 6") > -1) {
      this.fireFileDownloaded(download);
      this._win.openUrl("https://notion_content.presonusmusic.com/" + download.folder + "/" + download.filename);
    } else if (download.tokenized || download.type == "firmware") {
      this.productService
        .getTokenizedUrl(download.folder, download.filename, "my_fender", this.product.title, this.product.id, download.filename, download.filesize, download.version || download.version_number)
        .subscribe({
          next: (result: any) => {
            this.fireFileDownloaded(download);
            window.open(result.url);
          },
          error: (error) => {
            this.appService.alertError(error);
          },
        });
    } else {
      this.fireFileDownloaded(download);
      this._win.openUrl(download.url);
    }
    this.maybePromptMarketingOptIn();
  }

  private maybePromptMarketingOptIn() {
    if (this.product?.id !== environment.product_ids.fsp8demo) return;
    const settings = this.user.fender_email_settings || {
      fender_news: false,
      fender_news_intl: false,
      fender_studio_news: false,
      presonus_news: false,
    };
    const fenderNewsOn = this.user.country === "US" ? settings.fender_news : settings.fender_news_intl;
    if (fenderNewsOn && settings.fender_studio_news) return;

    this.appService
      .confirm(this.translate.instant("profile.demo.marketing_optin_mailing_list"), this.translate.instant("onboarding.signup"), this.translate.instant("general.no_thanks"))
      .then((confirmed) => {
        if (!confirmed) return;
        const args: any = { ...settings, fender_studio_news: true, demoSource: "DEMO" };
        if (this.user.country === "US") {
          args.fender_news = true;
        } else {
          args.fender_news_intl = true;
        }
        this.userService.saveIterableSettings(args).subscribe(() => {
          this.userService.getUserDetails(false).subscribe();
        });
      });
  }

  /**
   * Handle partner download URL clicks (e.g., Splice, EastWest).
   * Fires File Downloaded event and preserves existing GA tracking.
   */
  onPartnerDownloadClick() {
    // Fire File Downloaded event for partner downloads (only in My Fender)
    if (this.userService.appId === "fender" && this.product?.partnerDownloadUrl) {
      const syntheticDownload = {
        title: this.product.stringId === "splicebundle" ? "Start Trial" : "Redeem",
        url: this.product.partnerDownloadUrl,
      };
      this.fireFileDownloaded(syntheticDownload);
    }

    // Preserve existing GA tracking behavior
    this.ga.trackOutboundLink(this.product.partnerDownloadUrl);

    // Open the partner URL
    this._win.openUrl(this.product.partnerDownloadUrl);
  }

  openUrl(Url) {
    this._win.openUrl(Url);
  }
}
